import { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, Checkbox, Textarea, VStack, HStack, Text, Icon, useToast, Box, Flex, Avatar
} from '@chakra-ui/react';
import { FaBell, FaEnvelope, FaWhatsapp, FaPaperPlane } from 'react-icons/fa';
import { notifyTicket, getUsers } from '../../services/api';
import { semBloqueados } from '../../hooks/useUsuarios';

const getImgUser = (usuario_id) => {
    const map = {
        3: "/gabriel_logo.webp", 5: "/tilio_logo.webp", 6: "/riccioppo_logo.webp",
        7: "/user.png", 8: "/gabriel_valiente_logo.webp", 9: "/emerson_vendedor_logo.webp",
        10: "/analuiza_logo.webp"
    };
    return map[usuario_id] || "/user.png";
};

const TicketNotifyDialog = ({ isOpen, onClose, ticket }) => {
    const [sendEmail, setSendEmail] = useState(true);
    const [sendWhatsApp, setSendWhatsApp] = useState(true);
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const toast = useToast();

    // Fetch all users once
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await getUsers();
                setAllUsers(semBloqueados(response.data || []));
            } catch {}
        };
        if (isOpen && allUsers.length === 0) fetchUsers();
    }, [isOpen]);

    // Pre-select assignees + author when ticket changes (only those still in the list: blocked users are out)
    useEffect(() => {
        if (ticket && isOpen) {
            const ativos = new Set(allUsers.map(u => u.id));
            const ids = new Set();
            if (ativos.has(ticket.author?.id)) ids.add(ticket.author.id);
            ticket.assignees?.forEach(a => { if (ativos.has(a.id)) ids.add(a.id); });
            setSelectedUserIds(Array.from(ids));
        }
    }, [ticket, isOpen, allUsers]);

    const toggleUser = (userId) => {
        setSelectedUserIds(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const handleSend = async () => {
        if (!sendEmail && !sendWhatsApp) {
            toast({ title: "Selecione pelo menos um canal.", status: "warning", isClosable: true });
            return;
        }
        if (selectedUserIds.length === 0) {
            toast({ title: "Selecione pelo menos um destinatario.", status: "warning", isClosable: true });
            return;
        }

        setIsSending(true);
        try {
            const response = await notifyTicket(ticket.id, {
                send_email: sendEmail,
                send_whatsapp: sendWhatsApp,
                message: message || null,
                recipient_ids: selectedUserIds,
            });

            const sent = response.data.sent;
            let desc = [];
            if (sent.email > 0) desc.push(`${sent.email} email(s)`);
            if (sent.whatsapp > 0) desc.push(`${sent.whatsapp} WhatsApp(s)`);

            toast({
                title: "Notificacao enviada!",
                description: desc.length > 0 ? `Enviado: ${desc.join(', ')}` : 'Notificacao processada.',
                status: "success",
                isClosable: true,
            });

            setMessage('');
            onClose();
        } catch (error) {
            toast({
                title: "Erro ao enviar notificacao",
                description: error.response?.data?.message || "Tente novamente.",
                status: "error",
                isClosable: true,
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleClose = () => {
        if (!isSending) {
            setMessage('');
            onClose();
        }
    };

    // Identify which users are assignees/author for visual hints
    const assigneeIds = new Set(ticket?.assignees?.map(a => a.id) || []);
    const authorId = ticket?.author?.id;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size={{ base: 'full', sm: 'md' }}
            isCentered
            scrollBehavior="inside"
            motionPreset="slideInBottom"
        >
            <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
            <ModalContent
                borderRadius={{ base: 0, sm: 'xl' }}
                overflow="hidden"
                mx={{ base: 0, sm: 4 }}
                my={{ base: 0, sm: 'auto' }}
                maxH={{ base: '100dvh', sm: '90vh' }}
            >
                <Box h="3px" bgGradient="linear(to-r, #F68633, #E85D04)" />
                <ModalHeader pb={1}>
                    <HStack spacing={2}>
                        <Flex bg="orange.50" p={2} borderRadius="lg">
                            <Icon as={FaBell} color="orange.500" boxSize="16px" />
                        </Flex>
                        <Box>
                            <Text fontSize="md" fontWeight="700">Notificar Equipe</Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="normal">
                                Ticket #{ticket?.id} - {ticket?.subject?.substring(0, 40)}{ticket?.subject?.length > 40 ? '...' : ''}
                            </Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton isDisabled={isSending} />

                <ModalBody pt={2}>
                    <VStack spacing={4} align="stretch">
                        {/* Channels */}
                        <Box>
                            <Text fontSize="sm" fontWeight="600" color="gray.700" mb={2}>Canais de notificacao</Text>
                            <HStack spacing={4}>
                                <Checkbox isChecked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} colorScheme="blue" size="md">
                                    <HStack spacing={1.5}>
                                        <Icon as={FaEnvelope} color="blue.500" boxSize="13px" />
                                        <Text fontSize="sm">Email</Text>
                                    </HStack>
                                </Checkbox>
                                <Checkbox isChecked={sendWhatsApp} onChange={(e) => setSendWhatsApp(e.target.checked)} colorScheme="green" size="md">
                                    <HStack spacing={1.5}>
                                        <Icon as={FaWhatsapp} color="green.500" boxSize="13px" />
                                        <Text fontSize="sm">WhatsApp</Text>
                                    </HStack>
                                </Checkbox>
                            </HStack>
                        </Box>

                        {/* Recipients */}
                        <Box>
                            <Flex justify="space-between" align="center" mb={2}>
                                <Text fontSize="sm" fontWeight="600" color="gray.700">Destinatarios</Text>
                                <HStack spacing={2}>
                                    <Button size="xs" variant="link" colorScheme="blue" onClick={() => setSelectedUserIds(allUsers.map(u => u.id))}>
                                        Todos
                                    </Button>
                                    <Text fontSize="xs" color="gray.300">|</Text>
                                    <Button size="xs" variant="link" colorScheme="gray" onClick={() => setSelectedUserIds([])}>
                                        Nenhum
                                    </Button>
                                </HStack>
                            </Flex>
                            <div
                                className="taga-recipients-scroll"
                                style={{
                                    maxHeight: '260px',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    WebkitOverflowScrolling: 'touch',
                                    overscrollBehavior: 'contain',
                                    touchAction: 'pan-y',
                                }}
                                onWheelCapture={(e) => e.stopPropagation()}
                                onTouchMoveCapture={(e) => e.stopPropagation()}
                            >
                                <VStack align="stretch" spacing={0}>
                                    {allUsers.map(user => {
                                        const isSelected = selectedUserIds.includes(user.id);
                                        const isAssignee = assigneeIds.has(user.id);
                                        const isAuthor = user.id === authorId;

                                        return (
                                            <Flex
                                                key={user.id}
                                                align="center"
                                                px={2}
                                                py={{ base: 2.5, sm: 1.5 }}
                                                minH={{ base: '44px', sm: 'auto' }}
                                                borderRadius="md"
                                                cursor="pointer"
                                                bg={isSelected ? 'blue.50' : 'transparent'}
                                                _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
                                                _active={{ bg: isSelected ? 'blue.200' : 'gray.100' }}
                                                onClick={() => toggleUser(user.id)}
                                                transition="all 0.1s"
                                            >
                                                <Checkbox
                                                    isChecked={isSelected}
                                                    onChange={() => toggleUser(user.id)}
                                                    colorScheme="blue"
                                                    size="sm"
                                                    mr={2}
                                                    pointerEvents="none"
                                                />
                                                <Avatar size="xs" name={user.name} src={getImgUser(user.id)} mr={2} />
                                                <Text fontSize="sm" fontWeight={isSelected ? '600' : '400'} color={isSelected ? 'gray.800' : 'gray.600'} flex={1}>
                                                    {user.name}
                                                </Text>
                                                {isAuthor && (
                                                    <Text fontSize="2xs" color="orange.500" fontWeight="600" bg="orange.50" px={1.5} borderRadius="full">Autor</Text>
                                                )}
                                                {isAssignee && !isAuthor && (
                                                    <Text fontSize="2xs" color="blue.500" fontWeight="600" bg="blue.50" px={1.5} borderRadius="full">Atribuido</Text>
                                                )}
                                            </Flex>
                                        );
                                    })}
                                </VStack>
                            </div>
                            <Text fontSize="2xs" color="gray.400" mt={1}>{selectedUserIds.length} selecionado(s)</Text>
                        </Box>

                        {/* Message */}
                        <Box>
                            <Text fontSize="sm" fontWeight="600" color="gray.700" mb={1}>Mensagem personalizada</Text>
                            <Text fontSize="xs" color="gray.400" mb={2}>Opcional - se vazio, sera enviada uma mensagem padrao de lembrete.</Text>
                            <Textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Ex: Pessoal, precisamos resolver esse ticket ate sexta..."
                                bg="gray.50"
                                borderRadius="lg"
                                border="1px solid"
                                borderColor="gray.200"
                                _focus={{ borderColor: 'orange.400', boxShadow: '0 0 0 1px #F68633' }}
                                minH="70px"
                                fontSize="sm"
                                maxLength={2000}
                            />
                            {message.length > 0 && (
                                <Text fontSize="2xs" color="gray.400" textAlign="right" mt={1}>{message.length}/2000</Text>
                            )}
                        </Box>
                    </VStack>
                </ModalBody>

                <ModalFooter
                    borderTop="1px solid"
                    borderColor="gray.100"
                    flexDirection={{ base: 'column-reverse', sm: 'row' }}
                    gap={2}
                >
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClose}
                        isDisabled={isSending}
                        borderRadius="lg"
                        mr={{ base: 0, sm: 2 }}
                        w={{ base: 'full', sm: 'auto' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        colorScheme="orange"
                        size="sm"
                        leftIcon={<FaPaperPlane />}
                        onClick={handleSend}
                        isLoading={isSending}
                        loadingText="Enviando..."
                        borderRadius="lg"
                        isDisabled={(!sendEmail && !sendWhatsApp) || selectedUserIds.length === 0}
                        w={{ base: 'full', sm: 'auto' }}
                    >
                        Enviar Notificacao
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default TicketNotifyDialog;
